import {
  Box,
  Container,
  Header,
  SpaceBetween,
  Spinner,
  ColumnLayout,
  ExpandableSection,
  Button,
  Select,
  FormField,
  Input,
  Table,
  Icon,
  DatePicker,
  Link,
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { CodeViewer } from '../Components/Common/CodeViewer';
import { useIntegrationQuery } from './hooks/useIntegrationQuery';
import { useListDashboardsQuery } from './hooks/useListDashboardsQuery';

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <span
      style={{ cursor: 'pointer', marginLeft: 4 }}
      title="Copy to clipboard"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      <Icon name={copied ? 'status-positive' : 'copy'} size="small" />
    </span>
  );
};

interface IntegrationDetailsParams {
  clientId: string;
  datasourceName: string;
}

const AUTO_REFRESH_OPTIONS = [
  { label: 'Any', value: '' },
  { label: 'true', value: 'true' },
  { label: 'false', value: 'false' },
];

const IntegrationDetails: React.FC<RouteComponentProps<IntegrationDetailsParams>> = (props) => {
  const { clientId, datasourceName } = props.match.params;
  const history = useHistory();
  const [getIntegration, { loading, data: integrationData }] = useIntegrationQuery();
  const [getDashboards, { loading: dashLoading, data: dashData }] = useListDashboardsQuery();

  const [sortingColumn, setSortingColumn] = useState<any>({ sortingField: 'IndexName' });
  const [isDescending, setIsDescending] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(AUTO_REFRESH_OPTIONS[0]);
  const [createdAfter, setCreatedAfter] = useState('');
  const [indexState, setIndexState] = useState('');

  useEffect(() => {
    getIntegration({
      variables: { accountId: clientId, datasourceName },
    });
  }, [clientId, datasourceName, getIntegration]);

  const handleListDashboards = () => {
    getDashboards({
      variables: {
        clientId,
        datasource: datasourceName,
        autoRefresh: autoRefresh.value || undefined,
        createdAfter: createdAfter || undefined,
        indexState: indexState.trim() || undefined,
      },
    });
  };

  const datasources = get(integrationData, 'integration', []);
  const datasource = datasources[0];
  const dashboards = get(dashData, 'listDashboards', null);
  const dashboardList = Array.isArray(dashboards) ? dashboards : dashboards ? [dashboards] : [];

  const sortedDashboards = [...dashboardList].sort((a, b) => {
    const field = sortingColumn?.sortingField;
    if (!field) return 0;
    const aVal = String(a[field] || '');
    const bVal = String(b[field] || '');
    return isDescending ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
  });

  return (
    loading ? (
      <Box textAlign="center" margin={{ top: 'xxl' }}>
        <Spinner size="large" />
      </Box>
    ) : !datasource ? (
      <Container>
        <Box textAlign="center" color="inherit">
          <b>Datasource not found</b>
        </Box>
      </Container>
    ) : (
      <SpaceBetween size="l">
        <Header variant="h1">
          <span style={{ color: '#0972d3' }}>{datasource.DataSourceName}</span>
        </Header>
        
        <Container header={<Header variant="h2"><span style={{ color: '#0073bb' }}>Basic Information</span></Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <SpaceBetween size="l">
              <div>
                <Box variant="awsui-key-label">Datasource Name</Box>
                <div>{datasource.DataSourceName}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Client ID</Box>
                <div>{datasource.ClientId}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Connector</Box>
                <div>{datasource.Connector}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Description</Box>
                <div>{datasource.Description || 'N/A'}</div>
              </div>
            </SpaceBetween>
            <SpaceBetween size="l">
              <div>
                <Box variant="awsui-key-label">Created Time</Box>
                <div>{datasource.CreatedTime}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Last Updated Time</Box>
                <div>{datasource.LastUpdatedTime}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Datasource Role ARN</Box>
                <div>{datasource.DataSourceRoleArn}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Datasource ARN</Box>
                <Box variant="code">{datasource.DataSourceArn}</Box>
              </div>
            </SpaceBetween>
          </ColumnLayout>
        </Container>

        {datasource.Properties && (
          <Container header={<Header variant="h2"><span style={{ color: '#0073bb' }}>Properties</span></Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              {Object.entries(datasource.Properties).map(([key, value]) => (
                <div key={key}>
                  <Box variant="awsui-key-label">{key}</Box>
                  <div>{String(value)}</div>
                </div>
              ))}
            </ColumnLayout>
          </Container>
        )}

        <Container header={<Header variant="h2"><span style={{ color: '#0073bb' }}>Dashboards</span></Header>}>
          <SpaceBetween size="l">
            <ColumnLayout columns={4}>
              <FormField label="Auto Refresh">
                <Select
                  selectedOption={autoRefresh}
                  onChange={({ detail }) => setAutoRefresh(detail.selectedOption as any)}
                  options={AUTO_REFRESH_OPTIONS}
                />
              </FormField>
              <FormField label="Created After">
                <Input
                  value={createdAfter}
                  onChange={({ detail }) => setCreatedAfter(detail.value)}
                  placeholder="2025-04-18T09:32:11.000Z"
                />
              </FormField>
              <FormField label="Index State">
                <Input
                  value={indexState}
                  onChange={({ detail }) => setIndexState(detail.value)}
                  placeholder="e.g. active"
                />
              </FormField>
              <FormField label={<span>&nbsp;</span>}>
                <Button variant="primary" onClick={handleListDashboards} loading={dashLoading}>
                  List Dashboards
                </Button>
              </FormField>
            </ColumnLayout>

            {dashLoading && (
              <Box textAlign="center" padding={{ vertical: 'l' }}>
                <Spinner size="large" />
              </Box>
            )}

            {!dashLoading && dashboardList.length > 0 && (
              <Table
                columnDefinitions={[
                  {
                    id: 'indexName',
                    header: 'Index Name',
                    cell: (item: any) => (
                      <span>
                        <CopyButton text={item.IndexName} />
                        {' '}
                        <Link onFollow={() => history.push(`/integration/index-explorer?indexName=${encodeURIComponent(item.IndexName)}`)}>
                          {item.IndexName}
                        </Link>
                      </span>
                    ),
                    sortingField: 'IndexName',
                  },
                  {
                    id: 'autoRefresh',
                    header: 'Auto Refresh',
                    cell: (item: any) => item.AutoRefresh || 'N/A',
                    sortingField: 'AutoRefresh',
                  },
                  {
                    id: 'indexState',
                    header: 'Index State',
                    cell: (item: any) => item.IndexState || 'N/A',
                    sortingField: 'IndexState',
                  },
                  {
                    id: 'createTime',
                    header: 'Create Time',
                    cell: (item: any) => item.CreateTime || 'N/A',
                    sortingField: 'CreateTime',
                  },
                ]}
                items={sortedDashboards}
                stripedRows={true}
                sortingColumn={sortingColumn}
                sortingDescending={isDescending}
                onSortingChange={({ detail }) => {
                  setSortingColumn(detail.sortingColumn);
                  setIsDescending(detail.isDescending || false);
                }}
                header={<Header counter={`(${dashboardList.length})`}>Dashboards</Header>}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No dashboards found</b>
                  </Box>
                }
              />
            )}

            {!dashLoading && dashboards && dashboardList.length === 0 && (
              <Box textAlign="center" color="inherit" padding={{ vertical: 'l' }}>
                <b>No dashboards found for this datasource</b>
                <Box variant="p" color="text-body-secondary">
                  CLI: <Box variant="code">aes dqs list-dashboards --client-id {clientId} --datasource {datasourceName}</Box>
                </Box>
              </Box>
            )}
          </SpaceBetween>
        </Container>

        <ExpandableSection headerText="Full JSON Response" variant="container">
          <CodeViewer
            codeData={JSON.stringify(datasource, null, 2)}
            language={{ label: 'json', value: 'json' }}
            theme={{ label: 'coy', value: 'coy' }}
          />
        </ExpandableSection>
      </SpaceBetween>
    )
  );
};

export { IntegrationDetails };
